<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import {
  iBadge,
  iBadgeColours,
  iButton,
  iButtonTypes,
  iButtonSizes,
  iButtonStyles,
  iKeyIcon,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
  iTooltip,
  iTooltipAlignmentMode,
  showToast,
} from '@inscribe/signal';
import CustomerEmptyState from '@/V2/customers/CustomerEmptyState.vue';
import humanizeString from 'humanize-string';
import CustomerListLoadingBars from '@/V2/customers/CustomerListLoadingBars.vue';

export default {
  components: {
    CustomerEmptyState,
    ExpandableContainer,
    ExpandableContainerItem,
    iBadge,
    iButton,
    iTooltip,
    iKeyIcon,
    CustomerListLoadingBars,
  },
  props: {
    customerProfile: {
      type: Object,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    profileItemsConfig: {
      type: Object,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    showComponentIfEmpty: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      iBadgeColours,
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iTooltipAlignmentMode,
      iKeyIconSize,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
    };
  },
  computed: {
    profileItems() {
      return Object.keys(this.profileItemsConfig)
        .map((key) => {
          const itemConfig = this.profileItemsConfig?.[key];
          const itemData = this.getItemFromPath(
            this.customerProfile,
            itemConfig.path,
          );
          return itemData?.value || itemData?.[itemConfig?.value_field]
            ? {
                ...itemConfig,
                ...itemData,
                source: humanizeString(itemData?.source),
                label: itemConfig?.label || key,
                value:
                  itemData?.[itemConfig?.value_field] || itemData?.value || '',
              }
            : null;
        })
        .filter((item) => {
          return (
            item &&
            (item.label
              .toLowerCase()
              .includes(this.searchQuery.toLowerCase()) ||
              item.value.toLowerCase().includes(this.searchQuery.toLowerCase()))
          );
        });
    },
    showEmptyState() {
      return this.profileItems.length === 0 && !this.isLoading;
    },
    businessName() {
      return this.customerProfile?.company?.legal_name?.value;
    },
  },

  methods: {
    getItemFromPath(obj, path) {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    },
    copyToClipboard(value) {
      navigator.clipboard.writeText(value);
      showToast({
        title: 'Copied',
        text: `Value successfully copied`,
      });
    },
    onItemClick(item) {
      this.$emit('onItemClick', item);
    },
  },
};
</script>

<template>
  <template v-if="!showEmptyState || showComponentIfEmpty">
    <div class="customer-profile-company-items">
      <!-- empty state-->
      <customer-empty-state
        v-if="showEmptyState && showComponentIfEmpty"
        class="empty-state-container i-p-11"
        :title="
          searchQuery
            ? 'No matching results found'
            : 'No profile information available'
        "
        :description="
          searchQuery
            ? 'Try searching again with different keywords'
            : 'We couldn’t find any information for this profile'
        "
        :showButton="false"
        :icon="searchQuery ? 'medium-search' : 'medium-user-single'"
      />

      <ExpandableContainer
        v-else-if="!showEmptyState"
        :title="isLoading ? 'Business Details' : businessName"
        :subtitle="isLoading ? 'Loading...' : 'Business Details'"
      >
        <template #items>
          <div class="slot-content">
            <!-- loading state -->
            <customer-list-loading-bars
              v-if="isLoading"
              data-testid="customer-profile-company-items-loading"
            >
            </customer-list-loading-bars>

            <!-- loaded state -->
            <div v-else class="signals-container d-flex flex-column i-gap-1">
              <expandable-container-item
                v-for="(item, index) in profileItems"
                :key="item.name"
                :title="item.label"
                :subtitle="item.value"
                :reverse-fonts="true"
                @on-item-click="onItemClick(item)"
                @on-right-icon-click="onItemClick(item)"
              >
                <template #left-container>
                  <i-key-icon
                    :icon="item.icon"
                    :size="iKeyIconSize.SM"
                    :shape="iKeyIconShape.SQUARE"
                    :icon-style="iKeyIconStyle.OUTLINE"
                    :type="iKeyIconType.NEUTRAL"
                  />
                </template>

                <template #right-container>
                  <i-badge
                    v-if="item.source"
                    :colour="iBadgeColours.NEUTRAL"
                    icon="small-solid-info"
                    :has-leading-icon="true"
                    :label="item.source"
                    :outline="true"
                    :interactable="true"
                  ></i-badge>
                </template>

                <template #hover-right-container>
                  <!-- i need the id to have an index referring to the item -->
                  <i-button
                    left-icon="small_align_back"
                    :id="`copy-to-clipboard-button-${index}`"
                    :size="iButtonSizes.XSMALL"
                    :type="iButtonTypes.NEUTRAL"
                    :button-style="iButtonStyles.SOLID_LIGHT"
                    @button-click="copyToClipboard(item.value)"
                  />
                  <i-tooltip
                    :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
                    title="Copy"
                    :target="`#copy-to-clipboard-button-${index}`"
                  />
                </template>
              </expandable-container-item>
            </div>
          </div>
        </template>
      </ExpandableContainer>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.empty-state-container {
  border: 1px solid $light-neutral-60;
  border-radius: 12px;
}
</style>
